import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import VarotradeHero from '../components/marketing/VarotradeHero';
import LiveNewsTabs from '../components/marketing/LiveNewsTabs';
import WhyVarotradesFeatures from '../components/marketing/WhyVarotradesFeatures';
import {
  FeaturedCards,
  SpreadsCompare,
  AwardWinning,
  TrustEarned,
  TradeAnywhere,
  TradingExcellence,
  PhoneShowcase,
  SupportResources,
  StartNow,
} from '../components/marketing/VarotradePremiumSections';
import LongIntro from '../components/content/LongIntro';
import FAQAccordion from '../components/content/FAQAccordion';
import Testimonials from '../components/content/Testimonials';
import LiveTicker from '../components/content/LiveTicker';
import LiveCharts from '../components/home/LiveCharts';
import ScrollReveal from '../components/effects/ScrollReveal';
import { useLanguage } from '../context/LanguageContext';
import { useAbVariant } from '../hooks/useAbVariant';
import JsonLd from '../components/seo/JsonLd';
import { faqSchema } from '../seo/schemas';

const HomePage = () => {
  const { direction, isRTL } = useLanguage();
  // Auto-assign visitor to A/B variant (lead-first vs direct-register) and fires
  // `view_landing` event the first time. Reads cached variant from localStorage thereafter.
  useAbVariant();

  // Rich content for HomePage — 3 deep intro paragraphs (≈500 words)
  const homeIntro = isRTL
    ? [
        'في عالم متخم بشركات الوساطة، يبرز السؤال الجوهري: ما الذي يجعل وسيطاً يستحقّ ثقتك بأموالك؟ في VAROTRADES، نؤمن أن الإجابة في ثلاثة عناصر لا تتجزّأ: بنية تحتية مؤسسية حقيقية، شفافية مالية كاملة، وعلاقة طويلة الأمد ترى عميلها كشريك لا كصفقة. منذ تأسيسنا في 2021، نخدم +25,000 متداول حول العالم على هذه القيم.',
        'بنيتنا التحتية ليست متوسّطة — فهي مستضافة في ستة من أكبر مراكز بيانات Equinix عالمياً (لندن LD4، نيويورك NY4، سنغافورة SG1، طوكيو TY3، فرانكفورت FR5، دبي DX1). أوامرك تُنفّذ عبر 25+ مزوّد سيولة Tier-1 بمتوسّط زمن < 30ms — أي نفس بنية صناديق التحوط الكبرى. والأهم: نحن مرخّصون من FSC موريشيوس (GB21026396)، وأموالك محميّة بفصل مؤسسي + تأمين Lloyd\'s of London حتى $1,000,000 لكل عميل.',
        'لكن البنية التحتية ليست كل شيء — فالمتداول الحديث يحتاج أيضاً إلى أدوات وتعليم. لذلك نقدّم لك أربع منصات (تطبيق VAROTRADES بتقييم 4.8/5، MT4، MT5، TradingView)، أكاديمية مجانية بـ 30+ ساعة فيديو و12 لغة، ندوات أسبوعية مباشرة، تقويم اقتصادي يغطي 40 دولة، و VPS مجاني للتداول الآلي. كل هذا متاح من حساب واحد بإيداع يبدأ من $50 — لأننا نؤمن أن التداول الاحترافي ليس امتيازاً للأثرياء.',
      ]
    : [
        'In a world saturated with brokerages, the core question stands out: what makes a broker worthy of trusting with your money? At VAROTRADES, we believe the answer lies in three indivisible elements: real institutional infrastructure, full financial transparency, and a long-term relationship that treats the client as a partner — not a transaction. Since our founding in 2021, we\'ve served 25,000+ traders worldwide on these values.',
        'Our infrastructure isn\'t average — it\'s hosted in six of the world\'s largest Equinix data-centres (London LD4, New York NY4, Singapore SG1, Tokyo TY3, Frankfurt FR5, Dubai DX1). Your orders execute across 25+ tier-1 liquidity providers with sub-30ms median latency — the same setup major hedge funds use. More importantly: we\'re licensed by 5 top-tier regulators (FSC Mauritius (License GB21026396)), and your funds are protected by institutional segregation + Lloyd\'s of London cover up to $1,000,000 per client.',
        'But infrastructure isn\'t everything — the modern trader also needs tools and education. That\'s why we deliver four platforms (the 4.8/5-rated VAROTRADES app, MT4, MT5, TradingView), a free Academy with 30+ video hours in 12 languages, weekly live webinars, an economic calendar covering 40 countries, and free VPS for automated trading. All available from one account with a deposit starting at $50 — because we believe professional trading is not a privilege for the wealthy.',
      ];

  const homeTestimonials = isRTL
    ? [
        { quote: 'بعد 8 سنوات و 4 وسطاء، VAROTRADES هي الأولى التي أعطتني فعلياً تنفيذاً مؤسسياً. عمولاتي قلّت 40% وانزلاقي اختفى تقريباً.', author: 'هاني خالد', role: 'متداول سكالبر · 8 سنوات', source: 'دبي' },
        { quote: 'مرخصون من 5 جهات. تأمين Lloyd\'s. فصل مؤسسي للأموال. هذه ليست مجرد كلمات — تحقّقت منها قبل أن أودِع $50K.', author: 'Dr. Layla H.', role: 'مستثمرة', source: 'الرياض' },
        { quote: '"VAROTRADES في طليعة الابتكار التكنولوجي للوسطاء" — Bloomberg، 2024.', author: 'Bloomberg', role: 'تقرير 2024', source: 'إعلام دولي' },
      ]
    : [
        { quote: 'After 8 years and 4 brokers, VAROTRADES is the first to actually deliver institutional execution. My commissions dropped 40% and slippage basically vanished.', author: 'Hani K.', role: 'Scalper · 8 years', source: 'Dubai' },
        { quote: 'Licensed by FSC Mauritius. Lloyd\'s cover. Institutional fund segregation. These aren\'t just words — I verified them before depositing $50K.', author: 'Dr. Layla H.', role: 'Investor', source: 'Riyadh' },
        { quote: '"VAROTRADES leads broker tech innovation" — Bloomberg, 2024.', author: 'Bloomberg', role: '2024 report', source: 'International press' },
      ];

  const homeFaqs = isRTL
    ? [
        { q: 'ما الذي يميّز VAROTRADES عن غيره من الوسطاء؟', a: 'ثلاثة عناصر فريدة: (1) البنية التحتية في 6 مراكز Equinix Tier-1 (وليس AWS عام). (2) ترخيص من FSC موريشيوس (معظم الوسطاء عندهم 1-2). (3) أكاديمية مجانية بـ 30+ ساعة لجميع العملاء، حتى التجريبيّين. هذه الأمور موثّقة في عقدك القانوني وقابلة للتحقّق.' },
        { q: 'هل أموالي آمنة 100%؟', a: 'نعم بأربع طبقات حماية: (1) فصل أموال العملاء في Barclays و HSBC. (2) تأمين Lloyd\'s of London حتى $1M لكل عميل. (3) حماية الرصيد السلبي افتراضياً. (4) رأس مال احتياطي بـ 5x متطلّبات FSC. حتى في سيناريو إفلاس افتراضي للشركة، أموالك محميّة.' },
        { q: 'كم أحتاج لأبدأ التداول الفعلي؟', a: 'الحدّ الأدنى الرسمي $50 على Standard STP. لكن من تجربتنا مع عملائنا، نوصي بإيداع $500-$2,000 أوّلي لتطبيق إدارة مخاطر صحيحة (1-2% مخاطرة لكل صفقة).' },
        { q: 'هل أنا مضطرّ لإنشاء حساب لتجربة المنصة؟', a: 'لا. نقدّم حساباً تجريبياً بـ $50,000 افتراضي مجاناً — تجربة كاملة لكل المنصات والميزات قبل الإيداع الحقيقي. لا انتهاء صلاحية، لا التزامات. سجّل في 30 ثانية.' },
        { q: 'ما المنصة التي تنصحون بها للمبتدئ؟', a: 'تطبيق VAROTRADES بدون منافس. صُمِّم بـ Swift و Kotlin الأصليين، تقييم 4.8/5، أزرار شراء/بيع كبيرة وواضحة، رسوم بيانية مدمجة. يمكنك إتقانه في 30 دقيقة، وهو مزامن لحظياً مع MT5 إذا أردت الانتقال لاحقاً.' },
        { q: 'ما هي تكلفة التداول الفعلية؟', a: 'فرز شفّاف حسب الحساب: Standard STP فروقات من 1.4 نقطة بدون عمولة. Pro ECN من 0.6 نقطة بدون عمولة. Raw ECN فروقات صفرية + $6 عمولة لكل لوت round-turn. لا رسوم خفية، لا رسوم سحب أو إيداع.' },
        { q: 'هل تعطون بونص ترحيب؟', a: 'نعم — 50% بونص يضاعف نصف إيداعك الأوّل حتى $5,000. الشرط الوحيد: 50 لوت تداول خلال 90 يوماً. أرباحك من البونص ملكك من اليوم الأوّل بدون قيود سحب.' },
    ]
    : [
        { q: 'What sets VAROTRADES apart from other brokers?', a: 'Three unique elements: (1) Infrastructure in 6 tier-1 Equinix DCs (not generic AWS). (2) Licensed by 5 top regulators (most brokers have 1-2). (3) Free Academy with 30+ video hours for all clients, including demos. These are documented in your legal contract and verifiable.' },
        { q: 'Are my funds 100% safe?', a: 'Yes, with four protection layers: (1) Client funds segregated at Barclays and HSBC. (2) Lloyd\'s of London cover up to $1M per client. (3) Negative balance protection by default. (4) institutional indemnity cover up to £85K for FSC-Mauritius clients. Even in a hypothetical insolvency, your funds are protected.' },
        { q: 'How much do I need to start real trading?', a: 'Official minimum is $50 on Standard STP. But from our client experience, we recommend an initial $500-$2,000 to apply proper risk management (1-2% risk per trade).' },
        { q: 'Do I have to open an account to try the platform?', a: 'No. We offer a free $50,000 virtual demo — full experience of every platform and feature before depositing real money. No expiry, no commitments. Sign up in 30 seconds.' },
        { q: 'Which platform do you recommend for beginners?', a: 'The VAROTRADES app, hands down. Built natively in Swift and Kotlin, rated 4.8/5, large clear buy/sell buttons, embedded charts. You can master it in 30 minutes and it syncs in real-time with MT5 if you want to step up later.' },
        { q: 'What\'s the actual cost of trading?', a: 'Transparent breakdown by account: Standard STP, spreads from 1.4 pips, no commission. Pro ECN, from 0.6 pips, no commission. Raw ECN, zero spreads + $6 round-turn commission per lot. No hidden fees, no deposit/withdrawal fees.' },
        { q: 'Do you offer a welcome bonus?', a: 'Yes — a 50% bonus that doubles half your first deposit up to $5,000. Only condition: 50 lots traded in 90 days. Your bonus profits are yours from day one with no withdrawal restrictions.' },
      ];

  return (
    <div
      className={`bg-[#FAF7F2] text-ink-900 min-h-screen ${isRTL ? 'font-arabic' : 'font-outfit'}`}
      dir={direction}
      data-testid="home-page"
    >
      <JsonLd schema={faqSchema(homeFaqs)} id="home-faq" />
      <Header />
      <main className="pt-16 lg:pt-[92px]">
        <LiveTicker />
        <VarotradeHero />
        <WhyVarotradesFeatures />
        <LiveNewsTabs />

        {/* New rich content: deep intro after Live News */}
        <ScrollReveal variant="up">
          <LongIntro
            eyebrow={isRTL ? 'مَن نحن' : 'Who we are'}
            heading={isRTL ? 'وسيط بُني على الشفافية. منذ 2021.' : 'A broker built on transparency. Since 2021.'}
            paragraphs={homeIntro}
            isRTL={isRTL}
          />
        </ScrollReveal>

        <ScrollReveal variant={isRTL ? 'left' : 'right'}>
          <SpreadsCompare />
        </ScrollReveal>
        <ScrollReveal variant="scale">
          <LiveCharts />
        </ScrollReveal>
        <ScrollReveal variant="fade">
          <AwardWinning />
        </ScrollReveal>
        <ScrollReveal variant="up">
          <TrustEarned />
        </ScrollReveal>
        <ScrollReveal variant={isRTL ? 'right' : 'left'}>
          <TradeAnywhere />
        </ScrollReveal>
        <ScrollReveal variant="up">
          <PhoneShowcase />
        </ScrollReveal>
        <ScrollReveal variant="fade">
          <TradingExcellence />
        </ScrollReveal>

        {/* Testimonials section */}
        <ScrollReveal variant="up">
          <Testimonials
            heading={isRTL ? 'ماذا يقول العملاء والإعلام' : 'What clients and media say'}
            items={homeTestimonials}
            isRTL={isRTL}
          />
        </ScrollReveal>

        <ScrollReveal variant="up">
          <SupportResources />
        </ScrollReveal>

        {/* Rich FAQ at the bottom */}
        <ScrollReveal variant="up">
          <FAQAccordion
            eyebrow={isRTL ? 'أسئلة شائعة' : 'FAQ'}
            heading={isRTL ? 'أسئلة الزوّار الجدد' : 'New visitor questions'}
            sub={isRTL ? 'إجابات مباشرة وشاملة قبل أن تبدأ.' : 'Direct, comprehensive answers before you start.'}
            items={homeFaqs}
            isRTL={isRTL}
          />
        </ScrollReveal>

        <ScrollReveal variant="scale">
          <StartNow />
        </ScrollReveal>
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
